import React, { memo, useMemo, useCallback, useRef, useReducer, useEffect } from 'react';
import { useTheme, Icon, Text } from '@Sage/hoi-poi-ui';
import MapCircle from 'components/Map/MapCircle';
import MapMarkerWithTooltip from 'components/Map/MapMarkerWithTooltip';
import {
    AddressDisplay,
    MapDisplay,
} from 'containers/components/widgets/EntityLocation/components';
import Map from 'components/Map';
import { OPPORTUNITIES, COMPANIES } from 'constants/Entities';
import {
    CHECKIN_ICON,
    COMPANY_CHECKIN_ICON,
    OPPORTUNITY_CHECKIN_ICON,
    FAST_CHECKIN_ICON,
    COMPANY_ICON,
    OPPORTUNITY_ICON,
} from 'constants/Images';
import { CHECKIN_OUT_OF_RANGE_DISTANCE } from 'constants/Constants';
import { ACTIVITY_TYPE_OPPORTUNITY_CHECKIN } from 'models/ActivityModel';
import { getFMAddressFromCoordinates } from 'utils/map';
import { parseServerDecimal } from 'utils/fm';
import { getMomentFromDateBackend, getTimeOfMoment } from 'utils/dates';
import { getLiteral, getLiteralWithParametersHtml } from 'utils/getLiteral';

const areCoordinatesValid = (lat, lng) => lat !== '0' && lng !== '0' && lat && lng;

const ACTIONS = {
    SET_ADDRESS: 'SET_ADDRESS',
    SET_IS_LOADING: 'SET_IS_LOADING',
    SET_MARKERS: 'SET_MARKERS',
    SET_LOAD_MAP: 'SET_LOAD_MAP',
    SET_DISTANCE: 'SET_DISTANCE',
    SET_SHOW_WARNING: 'SET_SHOW_WARNING',
    RESET_WARNING: 'RESET_WARNING',
    SET_MAP_DATA: 'SET_MAP_DATA',
};

const initialState = {
    address: null,
    isLoading: true,
    markers: [],
    loadMap: undefined,
    distance: 0,
    showWarning: false,
};

const mapReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_ADDRESS:
            return { ...state, address: action.payload };
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload };
        case ACTIONS.SET_MARKERS:
            return { ...state, markers: action.payload };
        case ACTIONS.SET_LOAD_MAP:
            return { ...state, loadMap: action.payload };
        case ACTIONS.SET_DISTANCE:
            return { ...state, distance: action.payload };
        case ACTIONS.SET_SHOW_WARNING:
            return { ...state, showWarning: action.payload };
        case ACTIONS.RESET_WARNING:
            return { ...state, showWarning: false };
        case ACTIONS.SET_MAP_DATA:
            return {
                ...state,
                markers: action.payload.markers,
                distance: action.payload.distance,
                showWarning: action.payload.showWarning,
                isLoading: false,
            };
        default:
            return state;
    }
};

const DetailMap = memo(({ activity }) => {
    const mapComponentRef = useRef(null);
    const [state, dispatch] = useReducer(mapReducer, initialState);
    const { address, isLoading, markers, loadMap, distance, showWarning } = state;
    const oldActivityId = useRef(null);
    const theme = useTheme();

    const activityType = useMemo(
        () => parseInt(activity.ActivityType, 10),
        [activity.ActivityType],
    );

    const coordinates = useMemo(() => {
        if (!activity.Latitude || !activity.Longitude) return null;

        const lat = activity.Latitude.replace(',', '.');
        const lng = activity.Longitude.replace(',', '.');
        return {
            lat: parseFloat(lat),
            lng: parseFloat(lng),
        };
    }, [activity.Latitude, activity.Longitude]);

    const mapCenter = useMemo(
        () =>
            coordinates || {
                lat: 41.383032,
                lng: 2.118372,
            },
        [coordinates],
    );

    const onLoadMap = useCallback(() => {
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        getFMAddressFromCoordinates(coordinates)
            .then((result) => {
                if (result?.locationName || result?.googleLocationName) {
                    const fullAddress = result.locationName || result.googleLocationName;
                    dispatch({ type: ACTIONS.SET_ADDRESS, payload: fullAddress });
                }

                let mapMarkers = [];

                let activityMarker = {
                    key: activity.Id,
                    coordinates,
                    title: getTimeOfMoment(
                        getMomentFromDateBackend(activity.ActivityDate, null, true),
                    ),
                };

                switch (activity.IDTipoCheckin) {
                    case '1': //company checkin
                        activityMarker.icon = COMPANY_CHECKIN_ICON;
                        activityMarker.title = getLiteral('label_checkin');
                        break;
                    case '2': // opportunity checkin
                        activityMarker.icon = OPPORTUNITY_CHECKIN_ICON;
                        activityMarker.title = getLiteral('label_checkin');
                        break;
                    case '3':
                        activityMarker.icon = FAST_CHECKIN_ICON;
                        activityMarker.title = getLiteral('label_fast_checkin');
                        break;
                    default:
                        activityMarker.icon = CHECKIN_ICON;
                        activityMarker.title = getLiteral('label_activity');
                        break;
                }

                mapMarkers.push(activityMarker);

                const distance = !isNaN(activity?.distanceCheckin)
                    ? activity?.distanceCheckin
                    : parseInt(activity?.distanceCheckin, 10);

                let calculatedDistance = 0;
                let shouldShowWarning = false;

                // Helper function to add entity marker (reduces duplication)
                const addEntityMarker = (lat, lng, key, title, icon) => {
                    if (lat !== coordinates?.lat) {
                        calculatedDistance = distance;

                        if (distance > CHECKIN_OUT_OF_RANGE_DISTANCE) {
                            mapMarkers[0].icon = CHECKIN_ICON;
                            shouldShowWarning = true;
                        }

                        mapMarkers.push({
                            key,
                            title,
                            icon,
                            coordinates: { lat, lng },
                            hasCircle: true,
                        });
                    }
                };

                // Opportunity marker
                if (
                    areCoordinatesValid(
                        activity.OpportunityLatitude,
                        activity.OpportunityLongitude,
                    ) &&
                    activityType === ACTIVITY_TYPE_OPPORTUNITY_CHECKIN
                ) {
                    const lat = parseServerDecimal(activity.OpportunityLatitude);
                    const lng = parseServerDecimal(activity.OpportunityLongitude);
                    addEntityMarker(
                        lat,
                        lng,
                        activity.IdOpportunity,
                        activity.OpportunityName,
                        OPPORTUNITY_ICON,
                    );
                }
                // Company marker
                else if (areCoordinatesValid(activity.CompanyLatitude, activity.CompanyLongitude)) {
                    const lat = parseServerDecimal(activity.CompanyLatitude);
                    const lng = parseServerDecimal(activity.CompanyLongitude);
                    addEntityMarker(
                        lat,
                        lng,
                        activity.IdCompany,
                        activity.CompanyName,
                        COMPANY_ICON,
                    );
                }

                // Batch update to reduce re-renders
                dispatch({
                    type: ACTIONS.SET_MAP_DATA,
                    payload: {
                        markers: mapMarkers,
                        distance: calculatedDistance,
                        showWarning: shouldShowWarning,
                    },
                });
            })
            .catch((error) => {
                console.error('Error loading map data:', error);
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            });
    }, [
        activity.ActivityDate,
        activity.CompanyLatitude,
        activity.CompanyLongitude,
        activity.CompanyName,
        activity.IDTipoCheckin,
        activity.Id,
        activity.IdCompany,
        activity.IdOpportunity,
        activity.OpportunityLatitude,
        activity.OpportunityLongitude,
        activity.OpportunityName,
        activity.distanceCheckin,
        activityType,
        coordinates,
    ]);

    useEffect(() => {
        if (activity?.Id && activity.Id !== oldActivityId.current) {
            // This way we force to reload the map with the right markers of each activity
            dispatch({ type: ACTIONS.SET_LOAD_MAP, payload: onLoadMap });
            oldActivityId.current = activity.Id;
        }
    }, [activity?.Id, onLoadMap]);

    const circleProps = useMemo(() => {
        const markerWithCircle = markers.find((marker) => marker.hasCircle);
        return markerWithCircle
            ? {
                  radius: CHECKIN_OUT_OF_RANGE_DISTANCE,
                  center: markerWithCircle.coordinates,
                  options: {
                      strokeColor: theme.colors.grey200,
                      strokeOpacity: 0.2,
                      strokeWeight: 2,
                      fillColor: theme.colors.background.black005,
                      fillOpacity: 0.05,
                  },
              }
            : null;
    }, [markers, theme.colors.grey200, theme.colors.background.black005]);

    const handleDismissWarning = useCallback(() => {
        dispatch({ type: ACTIONS.SET_SHOW_WARNING, payload: false });
    }, []);

    const getWarning = useMemo(() => {
        if (distance <= CHECKIN_OUT_OF_RANGE_DISTANCE) return null;
        const literal =
            activity.IDTipoCheckin === '1'
                ? 'label_distance_checkin_account'
                : 'label_distance_checkin_opportunity';

        const warningText = getLiteralWithParametersHtml(literal, [distance], (item) => (
            <strong>{`${item}m`}</strong>
        ));

        return showWarning ? (
            <div className="fm-activities__advice">
                <div className="fm-activities__advice__content">
                    <div className="fm-activities__advice__content__container">
                        <div className="fm-activities__advice__content__text">
                            <Icon
                                className="fm-activities__advice__content__icon"
                                name="infoOutlined"
                                color={theme.colors.semantic.focusCustom600}
                            />
                            <Text type="captionMedium">{warningText}</Text>
                        </div>
                        <Icon
                            name="closeRaw"
                            color={theme.colors.semantic.focusCustom600}
                            size="small"
                            className="fm-activities__advice__dismiss"
                            onClick={handleDismissWarning}
                        />
                    </div>
                    <div>
                        <Text type="badges">
                            {`${getLiteral('label_distance_checkin_account_explanation')} `}
                            <a
                                className="fm-activities__advice__content__warning-link"
                                href={getLiteral('label_link_checkin_out_of_range_knowledge_base')}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {getLiteral('label_distance_checkin_account_explanation_cta_url')}
                            </a>
                        </Text>
                    </div>
                </div>
            </div>
        ) : null;
    }, [activity.IDTipoCheckin, distance, theme, showWarning, handleDismissWarning]);

    const geoAccuracy = useMemo(() => {
        const accuracy = activity.GeoAccuracy;
        return accuracy ? parseInt(accuracy, 10) : null;
    }, [activity.GeoAccuracy]);

    return (
        <div className="fm-activities__detail-map">
            <div className="fm-activities__detail-map__location">
                <Map
                    ref={mapComponentRef}
                    onLoadMap={loadMap}
                    center={mapCenter}
                    zoom={17}
                    disableDefaultUI
                    options={{
                        zoomControl: true,
                        mapTypeControl: true,
                        streetViewControl: true,
                        scaleControl: false,
                        rotateControl: false,
                        fullscreenControl: true,
                    }}
                    setIsLoading={(loading) =>
                        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: loading })
                    }
                    onUnmount={() => dispatch({ type: ACTIONS.RESET_WARNING })}
                >
                    {markers.map((marker) => (
                        <MapMarkerWithTooltip
                            key={marker.key}
                            position={marker.coordinates}
                            title={marker.title}
                            icon={marker.icon}
                        />
                    ))}
                    {circleProps && <MapCircle {...circleProps} />}
                </Map>
                {getWarning}
            </div>
            <div className="fm-activities__detail-map__address">
                <AddressDisplay
                    address={address}
                    loadingDistance={isLoading}
                    distance={distance}
                    coordinates={coordinates}
                    entity={
                        activityType === ACTIVITY_TYPE_OPPORTUNITY_CHECKIN
                            ? OPPORTUNITIES
                            : COMPANIES
                    }
                    geoAccuracy={geoAccuracy}
                    isFullCollapsible
                    preComponent={
                        <MapDisplay
                            isGeoLocated
                            latitude={activity.Latitude}
                            longitude={activity.Longitude}
                        />
                    }
                />
            </div>
        </div>
    );
});

export default DetailMap;
